import { useRouter } from "next/router";
import React, { useEffect, useState } from "react";
import AuthWrapper from "../../auth/AuthWrapper";
import { PublicNoteResponse, noteToClient } from "../../../shared/types";
import { PublicPage } from "../../components/PublicPage";
import { PublicEditor } from "../../components/PublicEditor";
import { useApiClient } from "../../api/client";

/**
 * This component displays a read only note that has been shared publicly.
 * A note will display with the favorite color scheme of the user, if they have an account.
 *
 * Loading is more complex than expected, as we need first to load the note, then load all entities that may be present in that note.
 * It doesn't handle yet <>, but could be added in a very symmetrical way; if we don't care about cascading <> previews inside the <>.
 *
 */

export default function SharedNotePage() {
  const router = useRouter();
  const noteId = router.query.noteId as string;
  const [result, setResult] = useState<PublicNoteResponse | null>(null);

  const apiClient = useApiClient();

  useEffect(() => {
    if (result || !noteId) return;
    apiClient.notes.getPublicNote(noteId).then((response) => {
      setResult(response);
    });
  }, [apiClient, noteId, result]);

  const note = result?.data?.note ? noteToClient(result.data.note) : undefined;
  const publicNotes = result?.data?.publicNotes.map(noteToClient);
  const allPublicNotes = [...(note ? [note] : []), ...(publicNotes || [])];
  const publicNoteIds = new Set(allPublicNotes?.map((n) => n.id));
  const linkedNotePreviews = result?.data?.linkedNotePreviews.filter((n) => !publicNoteIds.has(n.id)) || [];
  const loading = result === null;
  const error = result?.error;

  return (
    <AuthWrapper>
      <PublicPage>
        {error ? (
          error.includes("Note is not public") ? (
            <div>
              Note is not public. If it was just published, it may not be available yet. Please try again in a few
              seconds.
            </div>
          ) : error.includes("Note not found") ? (
            <div>
              Note is not public. If it was just published, it may not be available yet. Please try again in a few
              seconds.
            </div>
          ) : (
            <div>There was an error loading the note. Please try again in a few seconds.</div>
          )
        ) : loading ? (
          <div>Loading...</div>
        ) : (
          <PublicEditor
            publicNotes={allPublicNotes}
            linkedPrivatePreviews={linkedNotePreviews}
            visibleNotes={[note!.id]}
          />
        )}
      </PublicPage>
    </AuthWrapper>
  );
}
